/**
 * The wizard's sums and defaults, kept free of React so they can be tested:
 * land sizes, usable land, default animal ticks, the "fits nicely / may be cramped" counter,
 * "Surprise me" choices and the generator options for each layout.
 */
import { newId } from '../../../shared/plan-schema';
import { spaceFor, speciesNeeds } from '../../../shared/species-needs';
import type { Biome, GenerateOptions, PlanDoc, LayoutStyle, Pt, Species, SpeciesId, SpeciesPick, Theme, ThemeId, WaterStyle } from '../../../shared/types';

/** Extra space the wizard plans with (FD §4 default). */
export const EXTRA_SPACE_PCT = 25;
/** The most themes one zoo can have (FD §3.2). */
export const MAX_THEMES = 5;
/** How many themes "Surprise me" picks. */
export const SURPRISE_THEMES = 3;

/** The three blank-land sizes (FD §3.2 [A]: about 300 / 400 / 550 m across). */
export const SIZES = [
  { id: 'small', label: 'Small', size: 300 },
  { id: 'medium', label: 'Medium', size: 400 },
  { id: 'large', label: 'Large', size: 550 },
] as const;
export type SizeId = (typeof SIZES)[number]['id'];

/** Map size in metres for a size choice. */
export function sizeMetres(id: SizeId): number {
  return SIZES.find((s) => s.id === id)?.size ?? 400;
}

/**
 * Share of the zoo boundary the ticked animals may need for the counter to say "fits nicely", per map size.
 * Calibrated on generatePlan (D24): with 3–5 random themes and the animals ticked up to this share, every animal
 * was placed in at least 95% of 80 layouts per size, leaving room for paths, the plaza, water, shops, the back of
 * house and the packing's leftovers. Medium is lowest because its picks run to the most zones for its path length.
 */
export const USABLE_SHARE: Record<SizeId, number> = { small: 0.33, medium: 0.3, large: 0.33 };
/** The generator's blob plot covers about this share of the square map (radius 0.46 × size, Chaikin-smoothed). */
const BLOB_SHARE = 0.595;
/**
 * Land per habitat for "fits about N habitats": a typical habitat with its usual group plus 25% extra (median of the
 * data is ~810 m²) and its share of path frontage. Checked on generatePlan: 15 / 25 / 50 such habitats fit on
 * Small / Medium / Large in at least 90% of layouts.
 */
export const TYPICAL_HABITAT_M2 = 1200;

/** Polygon area in m² (shoelace). */
export function ringArea(ring: readonly Pt[]): number {
  let a = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const p = ring[i] as Pt, q = ring[j] as Pt;
    a += (q[0] + p[0]) * (q[1] - p[1]);
  }
  return Math.abs(a / 2);
}

/** The "fits nicely" share for a map size: the measured value at 300 / 400 / 550 m, straight lines in between. */
export function usableShare(size: number): number {
  const pts = SIZES.map((s) => [s.size, USABLE_SHARE[s.id]] as const);
  const first = pts[0] as readonly [number, number], last = pts[pts.length - 1] as readonly [number, number];
  if (size <= first[0]) return first[1];
  if (size >= last[0]) return last[1];
  for (let i = 1; i < pts.length; i++) {
    const [x1, y1] = pts[i] as readonly [number, number], [x0, y0] = pts[i - 1] as readonly [number, number];
    if (size <= x1) return y0 + ((y1 - y0) * (size - x0)) / (x1 - x0);
  }
  return last[1];
}

/** Land in m² the animals can need and still fit nicely: on a generated plot of `size`, or inside a traced boundary. */
export function usableLand(size: number, boundary?: readonly Pt[] | null): number {
  const plot = boundary && boundary.length >= 3 ? ringArea(boundary) : size * size * BLOB_SHARE;
  return plot * usableShare(size);
}

/** "about N" habitats for a piece of land, rounded to a friendly number. */
export function fitsAbout(usable: number): number {
  const n = usable / TYPICAL_HABITAT_M2;
  return n < 12 ? Math.max(1, Math.round(n)) : Math.round(n / 5) * 5;
}

/** "1,250" — whole square metres, rounded to a friendly step. */
export function formatM2(m2: number): string {
  const step = m2 >= 2000 ? 50 : m2 >= 200 ? 10 : 5;
  return (Math.round(m2 / step) * step).toLocaleString('en-GB');
}

/** Area a zone with `count` animals needs, including the wizard's extra space. */
export function needFor(sp: Species, count: number): number {
  return speciesNeeds(sp, count, EXTRA_SPACE_PCT).area;
}

/** Water a zone with `count` animals needs inside it (0 for none), including the extra space. */
export function waterFor(sp: Species, count: number): number {
  return sp.kind === 'aquarium' ? 0 : spaceFor(sp.water, count) * (1 + EXTRA_SPACE_PCT / 100);
}

/** One theme's animals as the wizard lists them: suggested species that exist, each shown once (first chosen theme wins). */
export interface ThemeAnimals {
  theme: Theme;
  species: Species[];
}

/** The animals per chosen theme, in the theme's order of preference; a species suggested by two themes shows under the first. */
export function animalsByTheme(themeIds: readonly ThemeId[], themes: readonly Theme[], species: readonly Species[]): ThemeAnimals[] {
  const byId = new Map(species.map((s) => [s.id, s]));
  const seen = new Set<SpeciesId>();
  const out: ThemeAnimals[] = [];
  for (const id of themeIds) {
    const theme = themes.find((t) => t.id === id);
    if (!theme) continue;
    const list: Species[] = [];
    for (const sid of theme.suggested) {
      const sp = byId.get(sid);
      if (!sp || seen.has(sid)) continue;
      seen.add(sid);
      list.push(sp);
    }
    out.push({ theme, species: list });
  }
  return out;
}

/** A species on the animals step: ticked or not, and how many animals. */
export interface PickState {
  on: boolean;
  count: number;
}
export type Picks = Record<SpeciesId, PickState>;

/** Share of the usable land the pre-ticked animals may fill, leaving room to add a few. */
export const DEFAULT_FILL = 0.8;

/**
 * Pre-ticks a sensible set for the land: the themes take turns, each adding its next suggestion while the summed
 * need stays within 80% of the usable land; every theme gets at least two. Counts are the typical group size.
 */
export function defaultPicks(groups: readonly ThemeAnimals[], usable: number): Picks {
  const picks: Picks = {};
  const budget = usable * DEFAULT_FILL;
  let used = 0;
  const longest = Math.max(0, ...groups.map((g) => g.species.length));
  for (let i = 0; i < longest; i++) {
    for (const g of groups) {
      const sp = g.species[i];
      if (!sp) continue;
      const count = sp.groupSize.typical;
      const need = needFor(sp, count);
      const on = used + need <= budget || i < 2;
      if (on) used += need;
      picks[sp.id] = { on, count };
    }
  }
  return picks;
}

/** The ticked animals as generator picks, in the order they are listed. */
export function pickedSpecies(groups: readonly ThemeAnimals[], picks: Picks): SpeciesPick[] {
  const out: SpeciesPick[] = [];
  for (const g of groups) {
    for (const sp of g.species) {
      const p = picks[sp.id];
      if (p?.on) out.push({ speciesId: sp.id, count: p.count });
    }
  }
  return out;
}

/** What the habitat counter shows. */
export interface Fit {
  zones: number;
  need: number;
  usable: number;
  /** need / usable. */
  share: number;
  verdict: 'empty' | 'nicely' | 'cramped';
}

/** Summed need of the ticked animals against the usable land (FD §3.2 step 4). */
export function fitOf(picks: readonly SpeciesPick[], species: readonly Species[], usable: number): Fit {
  const byId = new Map(species.map((s) => [s.id, s]));
  let need = 0;
  for (const p of picks) {
    const sp = byId.get(p.speciesId);
    if (sp) need += needFor(sp, p.count);
  }
  const share = usable > 0 ? need / usable : 0;
  return { zones: picks.length, need, usable, share, verdict: picks.length === 0 ? 'empty' : share <= 1 ? 'nicely' : 'cramped' };
}

/** "12 habitats", "1 habitat and 2 aviaries" — counted by kind. */
export function zonesLabel(picks: readonly SpeciesPick[], species: readonly Species[]): string {
  const byId = new Map(species.map((s) => [s.id, s]));
  const n = { habitat: 0, aviary: 0, aquarium: 0 };
  for (const p of picks) n[byId.get(p.speciesId)?.kind ?? 'habitat']++;
  const parts: string[] = [];
  if (n.habitat) parts.push(`${n.habitat} ${n.habitat === 1 ? 'habitat' : 'habitats'}`);
  if (n.aviary) parts.push(`${n.aviary} ${n.aviary === 1 ? 'aviary' : 'aviaries'}`);
  if (n.aquarium) parts.push(`${n.aquarium} ${n.aquarium === 1 ? 'aquarium' : 'aquariums'}`);
  if (!parts.length) return 'No animals yet';
  return parts.length === 1 ? (parts[0] as string) : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Small deterministic random numbers (mulberry32) for "Surprise me". */
export function randomFrom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A fresh seed for the generator (a positive 31-bit integer). */
export function newSeed(): number {
  return 1 + Math.floor(Math.random() * 0x7ffffff0);
}

const isAquarium = (t: Theme) => t.id.includes('aquarium');

/**
 * "Surprise me" for themes: three themes with a land theme first (the walk starts on land), and at most one
 * aquarium theme so the zoo isn't half fish tanks.
 */
export function surpriseThemes(themes: readonly Theme[], seed: number, count = SURPRISE_THEMES): ThemeId[] {
  const r = randomFrom(seed);
  const pool = themes.slice();
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [pool[i], pool[j]] = [pool[j] as Theme, pool[i] as Theme];
  }
  const out: Theme[] = [];
  let tanks = 0;
  for (const t of pool) {
    if (out.length >= count) break;
    if (isAquarium(t)) {
      if (tanks >= 1) continue;
      tanks++;
    }
    out.push(t);
  }
  out.sort((a, b) => Number(isAquarium(a)) - Number(isAquarium(b)));
  return out.map((t) => t.id);
}

export type LayoutChoice = LayoutStyle | 'surprise';
export type WaterChoice = WaterStyle | 'surprise';

const LAYOUTS: LayoutStyle[] = ['loop', 'cross', 'eight'];
const WATERS: WaterStyle[] = ['lake', 'river', 'both', 'none'];

/**
 * Turns "surprise me" into a real style for the `index`-th layout of the batch made from `batchSeed`: the path
 * styles take turns from a random start, so three surprise layouts show all three. Water is drawn per layout and
 * leans to lake / river / both, with "none" only now and then.
 */
export function resolveStyle(layout: LayoutChoice, water: WaterChoice, batchSeed: number, index: number): { layout: LayoutStyle; water: WaterStyle } {
  const start = Math.floor(randomFrom(batchSeed ^ 0x5bd1e995)() * 3);
  const r = randomFrom((batchSeed + index * 7919) ^ 0x1b873593);
  const l = layout === 'surprise' ? (LAYOUTS[(start + index) % 3] as LayoutStyle) : layout;
  const w = water === 'surprise' ? (WATERS[Math.floor(r() * 3.4)] ?? 'none') : water;
  return { layout: l, water: w };
}

/** Everything the wizard collected that the generator needs. */
export interface Choices {
  size: number;
  boundary: Pt[] | null;
  themes: ThemeId[];
  picks: SpeciesPick[];
  layout: LayoutChoice;
  water: WaterChoice;
}

/** Generator options for a batch of `n` layouts from `baseSeed` (seeds baseSeed, baseSeed+1, …). */
export function batchOptions(c: Choices, baseSeed: number, n = 3): GenerateOptions[] {
  return Array.from({ length: n }, (_, i) => {
    const seed = baseSeed + i;
    const style = resolveStyle(c.layout, c.water, baseSeed, i);
    return {
      size: c.size,
      plot: c.boundary ? { ring: c.boundary } : 'blob',
      speciesPicks: c.picks,
      themes: c.themes,
      layout: style.layout,
      water: style.water,
      seed,
      extraSpacePct: EXTRA_SPACE_PCT,
    };
  });
}

const BIOME_NAME: Record<Biome, string> = {
  tropical: 'Tropical',
  temperate: 'Temperate',
  grassland: 'Grassland',
  desert: 'Desert',
  taiga: 'Taiga',
  tundra: 'Tundra',
  aquatic: 'Water',
};

/** Friendly name of a biome for the theme cards. */
export function biomeName(b: Biome): string {
  return BIOME_NAME[b];
}

/** Plain names for the layout card captions. */
export const LAYOUT_NAME: Record<LayoutStyle, string> = { loop: 'One big loop', cross: 'Loop with a shortcut', eight: 'Figure-eight' };
export const WATER_NAME: Record<WaterStyle, string> = { lake: 'Lake', river: 'River', both: 'Lake and river', none: 'No water' };

/**
 * The doc with the picks that didn't fit added to its Unplaced tray (one entry per pick, ids from newId), so
 * everyone who opens the plan sees them. Counts are kept to the plan's 1–500.
 */
export function withUnplaced(doc: PlanDoc, picks: readonly SpeciesPick[], makeId: () => string = newId): PlanDoc {
  if (!picks.length) return doc;
  const unplaced = { ...doc.unplaced };
  for (const p of picks) {
    let id = makeId();
    while (id in unplaced) id = makeId();
    unplaced[id] = { speciesId: p.speciesId, count: Math.min(500, Math.max(1, Math.round(p.count) || 1)) };
  }
  return { ...doc, unplaced };
}
